import { EXTERNAL_LINKS, EXTERNAL_LINK_PROPS } from '@/shared/brand/links';

const LINK =
  'transition-colors duration-200 text-blue-600 hover:text-blue-700 ' +
  'focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:outline-none rounded-sm ' +
  'dark:text-blue-300 dark:hover:text-blue-200';

/** The separator between the legal links. Hidden below `md`, where they wrap into a row. */
function Dot() {
  return (
    <span aria-hidden className="hidden md:inline">
      •
    </span>
  );
}

/**
 * The strip at the foot of every signed-in page.
 *
 * <h2>Here rather than on a screen, like `PageScrollButtons` above it</h2>
 *
 * It belongs to the application, not to any one page: built on the screen that asks for it
 * first, it gets rebuilt on the next one and the two copies disagree about the year or the
 * version. The design mounts it in its own `AppLayout` for the same reason.
 *
 * <h2>It is outside `<main>` and outside the page's scale</h2>
 *
 * A `<footer>` nested inside `<main>` is a landmark inside the main landmark, which is what
 * "skip to content" exists to get past — so it is a sibling. `<main>` is `flex-1`, so it
 * pushes this down on a short page without the design's `mt-auto`.
 *
 * <p>And it is **not** scaled by `--ui-scale`. That variable scales a page's content, which
 * the design writes in absolute pixels; this is written in the ordinary type scale and
 * scaling it would shrink it twice over. The design's `AppFooter` sits outside its zoom for
 * the same reason.
 *
 * <h2>The year is read, not written</h2>
 *
 * `getFullYear()` at render. A literal is wrong every 1 January, quietly, and on a page
 * nobody looks at — which is exactly how a stale copyright line survives a year.
 */
export function AppFooter() {
  const year = new Date().getFullYear();

  return (
    <footer className="mt-auto border-t border-gray-200 bg-white dark:border-white/10 dark:bg-[#0c182d]">
      <div className="mx-auto max-w-7xl px-4 py-4 sm:px-6 lg:px-8">
        <div className="flex flex-col items-center justify-between gap-2 sm:flex-row sm:gap-0">
          <div className="flex flex-row gap-4 text-sm text-gray-500 dark:text-gray-400">
            <span>© {year} Xtremand. All rights reserved.</span>
            {/* Below `md` the version moves up here, where there is room for it. */}
            <span className="flex md:hidden">Version 1.0.0</span>
          </div>

          <div className="flex flex-row items-start gap-4 text-xs text-gray-500 md:items-center md:gap-0 md:space-x-6 dark:text-gray-400">
            <span className="hidden md:flex">Version 1.0.0</span>
            <Dot />
            <a href={EXTERNAL_LINKS.privacyPolicy} {...EXTERNAL_LINK_PROPS} className={LINK}>
              Privacy Policy
            </a>
            <Dot />
            <a href={EXTERNAL_LINKS.termsOfService} {...EXTERNAL_LINK_PROPS} className={LINK}>
              Terms of Service
            </a>
            <Dot />
            <a href={EXTERNAL_LINKS.cookiesPolicy} {...EXTERNAL_LINK_PROPS} className={LINK}>
              Cookies Policy
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}
